import React, { useState } from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import validator from "validator";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "../../../lib/config/firebaseInit";
import { toast } from "../../../utilities/toast";
import { resetPasswordLink, resetPasswordMail } from "../../../lib/webService/webService";
import { consumeReturnPath } from "../../../utilities/authRedirect";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Shared styles ────────────────────────────────────────────────────────────

const pageWrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
};

const card = {
  backgroundColor: "#fff",
  borderRadius: 18,
  border: "1px solid rgba(15,23,42,0.07)",
  padding: "28px 24px 24px",
  boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
};

const labelStyle = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "rgba(60,60,67,0.55)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "block",
  marginBottom: 6,
};

const inputStyle = {
  backgroundColor: "rgba(118,118,128,0.1)",
  border: "1px solid transparent",
  borderRadius: 10,
  padding: "11px 14px",
  fontSize: "0.9375rem",
  color: "#1c1c1e",
  width: "100%",
};

const primaryBtn = {
  width: "100%",
  borderRadius: 12,
  padding: "13px",
  fontSize: "0.9375rem",
  fontWeight: 600,
  border: "none",
};

// ─── Component ────────────────────────────────────────────────────────────────

const Login = () => {
  const [inputEmail, setInputEmail] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ isErr: false, message: "" });
  const [btnResetLoading, setBtnResetLoading] = useState(false);
  const [switchState, setSwitchState] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = () => setSwitchState((s) => !s);

  async function login(email, password) {
    try {
      const persistence = switchState ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      if (user) {
        await user.getIdToken(true);
        const { claims } = await user.getIdTokenResult();

        let destination = "/";
        if (claims.elite)      destination = "/sup/";
        else if (claims.operations) destination = "/ops";
        else if (claims.sales)      destination = "/sales/dashboard";
        else if (claims.merchant)   destination = "/store/dashboard";

        const returnPath = consumeReturnPath();

        toast.success("Welcome back! Logging you in...");
        navigate(returnPath ?? destination, { replace: true });
      }
    } catch (error) {
      const messages = {
        "auth/invalid-email":                          "Invalid email address format",
        "auth/user-disabled":                          "This account has been disabled",
        "auth/user-not-found":                         "No account found with this email",
        "auth/wrong-password":                         "Incorrect password",
        "auth/invalid-credential":                     "Invalid email or password",
        "auth/too-many-requests":                      "Too many failed attempts. Try again later",
        "auth/network-request-failed":                 "Network error. Check your connection",
        "auth/operation-not-allowed":                  "Email/password sign-in is not enabled",
        "auth/account-exists-with-different-credential": "Account exists with different credentials",
        "auth/requires-recent-login":                  "Please sign in again to continue",
      };
      toast.error(messages[error.code] ?? "An error occurred. Please try again");
      setLoading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError({ isErr: false, message: "" });

    if (!inputEmail || !inputPassword) {
      setError({ isErr: true, message: "All fields are required" });
      return;
    }
    if (!validator.isEmail(inputEmail)) {
      setError({ isErr: true, message: "Invalid email format" });
      return;
    }
    if (inputPassword.length < 6) {
      setError({ isErr: true, message: "Minimum password length is 6" });
      return;
    }
    setLoading(true);
    await login(inputEmail, inputPassword);
  };

  const resetPassword = async () => {
    if (!inputEmail) { toast.error("Enter your email first"); return; }
    if (!validator.isEmail(inputEmail)) { toast.error("Invalid email format"); return; }
    try {
      setBtnResetLoading(true);
      const link = await resetPasswordLink(inputEmail);
      // await resetPasswordMail(inputEmail, link);
      toast.info(`Reset instructions sent to ${inputEmail}`);
    } catch (error) {
      const messages = {
        "auth/invalid-email":      "Invalid email address format",
        "auth/user-not-found":     "No account found with this email",
        "auth/too-many-requests":  "Too many attempts. Try again later",
        "auth/network-request-failed": "Network error. Check your connection",
      };
      toast.error(messages[error.code] ?? "Failed to send reset email");
    } finally {
      setBtnResetLoading(false);
    }
  };

  return (
    <div style={pageWrap}>
      <Seo {...generateNoIndexSEO("Log In")} />
      <div style={{ width: "100%", maxWidth: 380 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/imgs/logo/complete-250.png" alt="FineEats" style={{ height: "clamp(88px, 24vw, 120px)", width: "auto", maxWidth: "100%" }} />
        </div>

        {/* Card */}
        <div style={card}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em", textAlign: "center", marginBottom: 4 }}>
            Sign In
          </h1>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", textAlign: "center", marginBottom: 22 }}>
            Welcome back
          </p>

          {error.isErr && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError({ isErr: false, message: "" })}
              style={{ borderRadius: 10, fontSize: "0.875rem", marginBottom: 18, padding: "10px 14px" }}
            >
              {error.message}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email</label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  autoComplete="current-password"
                  value={inputPassword}
                  onChange={(e) => setInputPassword(e.target.value)}
                  required
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "rgba(60,60,67,0.4)", lineHeight: 1 }}
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot password */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
              <Form.Check
                type="switch"
                id="remember-me"
                label={<span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.65)" }}>Remember me</span>}
                defaultChecked={switchState}
                onChange={handleChange}
              />
              {btnResetLoading ? (
                <span style={{ fontSize: "0.875rem", color: "#007aff", display: "flex", alignItems: "center", gap: 5 }}>
                  <Spinner size="sm" animation="border" />Sending…
                </span>
              ) : (
                <button type="button" onClick={resetPassword} style={{ background: "none", border: "none", fontSize: "0.875rem", color: "#007aff", cursor: "pointer", padding: 0, fontWeight: 500 }}>
                  Forgot password?
                </button>
              )}
            </div>

            {/* Submit */}
            <Button type="submit" variant="primary" disabled={loading} style={primaryBtn}>
              {loading
                ? <><Spinner size="sm" animation="border" className="me-2" />Signing in…</>
                : "Sign In"
              }
            </Button>
          </Form>
        </div>

        {/* Footer */}
        <p style={{ textAlign: "center", marginTop: 18, fontSize: "0.875rem", color: "rgba(60,60,67,0.6)" }}>
          Don't have an account?{" "}
          <Link to="/register" style={{ color: "#007aff", textDecoration: "none", fontWeight: 500 }}>
            Create account
          </Link>
        </p>

      </div>
    </div>
  );
};

export default Login;
